import { useQuery } from '@tanstack/react-query';
import useUser from '../global/useUser';
import { getGroupedPayments, getPaymentsGroup } from '../../services/payments';
import { SortValues } from '../../types/ui';
import { PaymentsGroup, SortableKeys } from '../../types/payments';

function useGetGroupedPayments({
  page = 1,
  sort,
  selectedPaymentsGroup,
  setSelectedPaymentsGroup,
}: {
  page?: number;
  sort?: SortValues<SortableKeys>;
  selectedPaymentsGroup?: PaymentsGroup | null;
  setSelectedPaymentsGroup?: (group: PaymentsGroup) => void;
} = {}) {
  const { user } = useUser();

  const { data, isFetching, isLoading, isError, error, refetch } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      'grouped-payments',
      {
        userId: user?.userId,
        businessId: user?.business?.id,
        page,
        sortOrder: sort?.order,
        sortOrderBy: sort?.orderBy,
      },
    ],
    queryFn: async () => {
      const result = await getGroupedPayments({
        businessId: user!.business!.id,
        page,
        sort,
      });

      if (selectedPaymentsGroup && setSelectedPaymentsGroup) {
        const updatedPaymentsGroup =
          result.groups.find(
            group => group.groupKey === selectedPaymentsGroup.groupKey,
          ) ||
          (await getPaymentsGroup({
            groupPaymentsId: selectedPaymentsGroup.groupKey,
          }).catch(() => null));
        if (updatedPaymentsGroup) {
          setSelectedPaymentsGroup(updatedPaymentsGroup);
        }
      }

      return result;
    },
    enabled: !!user?.userId && !!user?.business?.id,
  });

  return {
    data,
    isFetching,
    isLoading,
    isError,
    error,
    refetch,
    sort,
  };
}

export default useGetGroupedPayments;
